import DateUtils from "src/services/utils/DateUtils";
import { INTERNATIONAL_DATE_FORMAT } from "src/common/constants";
import {
    buildReportHeaderSection,
    formatPdfDisplay,
    formatPdfMoney,
    formatPdfYesNo,
    keyValueSection,
    recordsSection,
} from "./providerPdfShared";

const dateUtils = new DateUtils();

const LocalActivityCodeTypes = {
    "1": "HIGH LEVEL ACTIVITY CODE",
    "10": "NAF (NOMENCLATURE D’ACTIVITE FRANCAISE)",
    "15": "NAF (NOMENCLATURE D’ACTIVITE FRANCAISE) VER2",
    "20": "BELGIUM NACE CODE",
    "25": "BELGIUM NACE CODE 2008",
    "40": "NETHERLAND KVK ACTIVITY CODE",
    "45": "ITALY NACE CODE",
    "50": "SBA CODE",
    "60": "1972 SIC CODE",
    "62": "ISIC",
    "70": "1987 SIC CODE",
    "80": "US GOVT 1977 SIC (MODIFIED IN 1981)",
    "90": "CANADIAN GOVERNMENT SIC",
    "95": "NORWEGIAN NACE CODE",
    "97": "NORWEGIAN NACE CODE REV2",
    "100": "MODERATE LEVEL ACTIVITY CODE",
    "110": "US D&B SIC PLUS 2",
    "112": "NORTH AMERICAN INDUSTRIAL CLSFN SYSTEM",
    "120": "CANADIAN D&B SIC PLUS 4",
    "130": "CANADIAN GOVERNMENT SIC PLUS 4",
    "140": "US GOVERNMENT 2 DIGIT 1977 SIC CODE",
    "150": "ASIA PACIFIC SIC CODE",
    "160": "FINNISH NACE CODE",
    "170": "FINNISH NACE CODE REV2",
    "180": "LUXEMBOURG NACE",
    "200": "DETAILED LEVEL ACTIVITY CODE",
    "210": "US D&B SIC 2 PLUS 2",
    "250": "DNB HYLDAHL NACE",
    "260": "UK FIVE DIGIT SIC",
    "300": "EUROPEAN NACE CODE",
    "305": "US GOVERNMENT 3 DIGIT 1977 SIC CODE",
    "310": "3 DIGIT 1987 SIC CODE",
    "315": "2 DIGIT 1987 SIC CODE",
    "320": "SWEDEN NACE CODE",
    "340": "UK Standard Industry Code 2007",
    "350": "NACE REV1",
    "360": "NACE REVISION 2",
    "370": "SWEDEN NACE CODE REV2",
    "380": "NETHERLANDS SBI CODE 2008",
    "390": "DANISH AUTHORITY NACE REV2",
    "450": "NETHERLANDS NACE CODE",
};

const SHAREHOLDER_TYPES = ["domestic", "headquarters", "global"];

const parseProviderDate = (dateValue) => {
    if (dateValue === null || dateValue === undefined || dateValue === "") {
        return null;
    }

    const raw = String(dateValue).trim();
    if (/^\d{8}$/.test(raw)) {
        const year = Number(raw.slice(0, 4));
        const month = Number(raw.slice(4, 6)) - 1;
        const day = Number(raw.slice(6, 8));
        const parsed = new Date(year, month, day);

        if (
            parsed.getFullYear() === year
            && parsed.getMonth() === month
            && parsed.getDate() === day
        ) {
            return parsed;
        }
    }

    return null;
};

const convertApiDate = (dateStr) => {
    if (dateStr === null || dateStr === undefined || dateStr === "") {
        return "-";
    }

    const providerDate = parseProviderDate(dateStr);
    if (providerDate) {
        return dateUtils.convertDateToDate(providerDate, INTERNATIONAL_DATE_FORMAT);
    }

    return dateUtils.convertDateToDate(dateStr, INTERNATIONAL_DATE_FORMAT);
};

const formatCountry = (company) => {
    if (!company?.country) {
        return "-";
    }
    return `${company.country}${company.countryIso ? ` (${company.countryIso})` : ""}`;
};

const formatSicActivityCodes = (codes) => {
    if (!Array.isArray(codes) || !codes.length) {
        return "-";
    }
    return codes
        .map((aCode) => `${aCode.code} - ${aCode.description}`)
        .join("; ");
};

const formatLocalActivityCodeType = (type) => {
    if (type === null || type === undefined || type === "") {
        return "-";
    }
    const label = LocalActivityCodeTypes[String(type)];
    return label ? `${type} (${label})` : String(type);
};

const buildCompanyInformationSections = (company) => {
    if (!company) {
        return [];
    }

    return [
        keyValueSection("Identity & Legal Info", [
            { title: "Primary Name", details: formatPdfDisplay(company.primaryName) },
            { title: "Trade Name", details: formatPdfDisplay(company.tradeName) },
            { title: "Former Primary Name", details: formatPdfDisplay(company.formerPrimaryName) },
            { title: "DUNS Number", details: formatPdfDisplay(company.duns) },
            { title: "Former DUNS Number", details: formatPdfDisplay(company.formerDuns) },
            { title: "Business Number", details: formatPdfDisplay(company.businessNumber) },
            { title: "Business Number Type", details: formatPdfDisplay(company.businessNumberType) },
        ]),
        keyValueSection("Establishment Address", [
            { title: "Street", details: formatPdfDisplay(company.establishmentAddress) },
            { title: "Postcode", details: formatPdfDisplay(company.establishmentAddressPostCode) },
            { title: "City", details: formatPdfDisplay(company.establishmentAddressCity) },
            { title: "Region", details: formatPdfDisplay(company.establishmentAddressRegion) },
            { title: "Country", details: formatPdfDisplay(company.establishmentAddressCountry) },
            { title: "Region Short", details: formatPdfDisplay(company.establishmentAddressRegionShort) },
            { title: "Region Code", details: formatPdfDisplay(company.establishmentAddressRegionCode) },
            { title: "Country ISO", details: formatPdfDisplay(company.establishmentAddressCountryIso) },
            {
                title: "Country DNB Code",
                details: formatPdfDisplay(company.establishmentAddressCountryDnb),
            },
            {
                title: "Deliverability Indication",
                details: formatPdfYesNo(!!company.establishmentDeliverabilityIndication),
            },
            {
                title: "Correspondence Deliverability Indication",
                details: formatPdfYesNo(!!company.correspondenceDeliverabilityIndication),
            },
            {
                title: "Correspondence Address",
                details: formatPdfDisplay(company.correspondenceAddress),
            },
        ]),
        keyValueSection("Correspondence Address", [
            { title: "Address", details: formatPdfDisplay(company.correspondenceAddress) },
            {
                title: "Deliverability Indication",
                details: formatPdfYesNo(!!company.correspondenceDeliverabilityIndication),
            },
        ]),
        keyValueSection("Contact Information", [
            { title: "Country Calling Code", details: formatPdfDisplay(company.countryCallingCode) },
            { title: "Phone Number", details: formatPdfDisplay(company.phoneNumber) },
            { title: "Fax Number", details: formatPdfDisplay(company.faxNumber) },
        ]),
        keyValueSection("Business Details", [
            { title: "Line of Business", details: formatPdfDisplay(company.lineOfBusiness) },
            { title: "Legal Form", details: formatPdfDisplay(company.legalForm) },
            { title: "Location Status", details: formatPdfDisplay(company.locationStatus) },
            {
                title: "Subsidiary Location",
                details: formatPdfYesNo(!!company.isSubsidiaryLocation),
            },
            {
                title: "Marketability Indication",
                details: formatPdfDisplay(company.marketabilityIndication),
            },
            {
                title: "Family Member Count",
                details: formatPdfDisplay(company.familyMemberCount),
            },
            {
                title: "Linkage Report Date",
                details: company.linkageReportDate
                    ? convertApiDate(company.linkageReportDate)
                    : "-",
            },
        ]),
    ].filter(Boolean);
};

const buildFinancialSummarySections = (summary) => {
    if (!summary || typeof summary !== "object") {
        return [];
    }

    const statementDate = convertApiDate(summary.statementDate);

    return [
        keyValueSection(`Business Activity Codes (${statementDate})`, [
            {
                title: "SIC Activity Codes",
                details: formatSicActivityCodes(summary.sicActivityCodes),
            },
            {
                title: "SIC8 Activity Codes",
                details: formatPdfDisplay(summary.sic8ActivityCodes),
            },
            {
                title: "Local Activity Code",
                details: formatPdfDisplay(summary.localActivityCode),
            },
            {
                title: "Local Activity Code Type",
                details: formatLocalActivityCodeType(summary.localActivityCodeType),
            },
        ]),
        keyValueSection(`Financial Summary (${statementDate})`, [
            {
                title: "Annual Sales (Local Currency)",
                details: formatPdfMoney(summary.annualSales, "EUR"),
            },
            {
                title: "Annual Sales (USD)",
                details: formatPdfMoney(summary.annualSalesUs, "USD"),
            },
            { title: "Currency", details: formatPdfDisplay(summary.currency) },
            {
                title: "Net Worth (Local Currency)",
                details: formatPdfMoney(summary.netWorth, "EUR"),
            },
            {
                title: "Net Worth (USD)",
                details: formatPdfMoney(summary.netWorthUs, "USD"),
            },
            {
                title: "Net Income (Local Currency)",
                details: formatPdfMoney(summary.netIncome, "EUR"),
            },
            {
                title: "Net Income (USD)",
                details: formatPdfMoney(summary.netIncomeUs, "USD"),
            },
        ]),
        keyValueSection(`Workforce (${statementDate})`, [
            { title: "Number of Employees", details: formatPdfDisplay(summary.employees) },
            { title: "Total Employees", details: formatPdfDisplay(summary.employeesTotal) },
        ]),
        keyValueSection(`Statement Metadata (${statementDate})`, [
            { title: "Start Year", details: formatPdfDisplay(summary.startYear) },
            { title: "Statement Date", details: convertApiDate(summary.statementDate) },
            { title: "Report Date", details: convertApiDate(summary.reportDate) },
            { title: "Out of Business", details: formatPdfYesNo(!!summary.outOfBusiness) },
            { title: "Deleted Record", details: formatPdfYesNo(!!summary.deleted) },
        ]),
    ].filter(Boolean);
};

const buildManagementTeamSection = (team) => {
    if (!Array.isArray(team) || !team.length) {
        return null;
    }

    return recordsSection(
        "Management Team",
        [
            { key: "fullName", label: "Full Name", width: "16%" },
            { key: "positions", label: "Position / Role", width: "14%" },
            { key: "pepsStatus", label: "PEP Status", width: "10%" },
            { key: "sanctionStatus", label: "Sanction Status", width: "12%" },
            { key: "sanctionHits", label: "Sanction Hits", width: "10%" },
            { key: "dateOfBirth", label: "Date of Birth", width: "10%" },
            { key: "title", label: "Title", width: "8%" },
            { key: "isActive", label: "Active", width: "6%" },
            { key: "appointmentDate", label: "Appointment Date", width: "10%" },
            { key: "monitoring", label: "Monitoring", width: "8%" },
        ],
        team.map((person, index) => ({
            id: person.id || `mgmt-${index}`,
            fullName: formatPdfDisplay(person.fullName),
            positions: formatPdfDisplay(person.positions),
            pepsStatus: formatPdfDisplay(person.pepsStatus),
            sanctionStatus: formatPdfDisplay(person.sanctionStatus),
            sanctionHits: formatPdfDisplay(person.sanctionHits),
            dateOfBirth: convertApiDate(person.dateOfBirth),
            title: formatPdfDisplay(person.title),
            isActive: formatPdfYesNo(!!person.isActive),
            appointmentDate: convertApiDate(person.appointmentDate),
            monitoring: formatPdfYesNo(!!person.has_monitoring_follow_list_item),
        }))
    );
};

const buildShareholdersSection = (shareholders) => {
    const team = (Array.isArray(shareholders) ? shareholders : [])
        .filter((holder) => SHAREHOLDER_TYPES.includes(holder.companyType));

    if (!team.length) {
        return null;
    }

    return recordsSection(
        "Shareholders",
        [
            { key: "name", label: "Company Name", width: "16%" },
            { key: "duns", label: "DUNS Number", width: "12%" },
            { key: "companyType", label: "Company Type", width: "10%" },
            { key: "address", label: "Street Address", width: "14%" },
            { key: "postcode", label: "Postal Code", width: "8%" },
            { key: "city", label: "City", width: "8%" },
            { key: "region", label: "Region / State", width: "10%" },
            { key: "regionShort", label: "Region Short", width: "8%" },
            { key: "regionCode", label: "Region Code", width: "8%" },
            { key: "country", label: "Country", width: "10%" },
        ],
        team.map((company, index) => ({
            id: company.id || `shareholder-${index}`,
            name: formatPdfDisplay(company.name),
            duns: formatPdfDisplay(company.duns),
            companyType: formatPdfDisplay(company.companyType),
            address: formatPdfDisplay(company.address),
            postcode: formatPdfDisplay(company.postcode),
            city: formatPdfDisplay(company.city),
            region: formatPdfDisplay(company.region),
            regionShort: formatPdfDisplay(company.regionShort),
            regionCode: formatPdfDisplay(company.regionCode),
            country: formatCountry(company),
        }))
    );
};

const buildLegalEntitiesSection = (shareholders) => {
    const team = (Array.isArray(shareholders) ? shareholders : [])
        .filter((holder) => holder.companyType === "global");

    if (!team.length) {
        return null;
    }

    return recordsSection(
        "Related Legal Entities",
        [
            { key: "name", label: "Company Name", width: "18%" },
            { key: "duns", label: "DUNS Number", width: "12%" },
            { key: "address", label: "Street Address", width: "14%" },
            { key: "postcode", label: "Postal Code", width: "8%" },
            { key: "city", label: "City", width: "10%" },
            { key: "region", label: "Region / State", width: "10%" },
            { key: "regionShort", label: "Region Short", width: "8%" },
            { key: "regionCode", label: "Region Code", width: "8%" },
            { key: "country", label: "Country", width: "12%" },
        ],
        team.map((company, index) => ({
            id: company.id || `legal-entity-${index}`,
            name: formatPdfDisplay(company.name),
            duns: formatPdfDisplay(company.duns),
            address: formatPdfDisplay(company.address),
            postcode: formatPdfDisplay(company.postcode),
            city: formatPdfDisplay(company.city),
            region: formatPdfDisplay(company.region),
            regionShort: formatPdfDisplay(company.regionShort),
            regionCode: formatPdfDisplay(company.regionCode),
            country: formatCountry(company),
        }))
    );
};

/**
 * Build PDF sections matching the Dun & Bradstreet KYB detail page.
 * Omits Shareholders Graph (visual-only) and screening/monitoring action controls.
 */
export const buildDnbExportSections = (details) => {
    if (!details || typeof details !== "object") {
        return [];
    }

    const sections = [];

    const headerSection = buildReportHeaderSection(details.header);
    if (headerSection) {
        sections.push(headerSection);
    }

    sections.push(...buildCompanyInformationSections(details.company));
    sections.push(...buildFinancialSummarySections(details.financial_summary));

    const management = buildManagementTeamSection(details.management_team);
    if (management) {
        sections.push(management);
    }

    const shareholders = buildShareholdersSection(details.company_shareholders);
    if (shareholders) {
        sections.push(shareholders);
    }

    const legalEntities = buildLegalEntitiesSection(details.company_shareholders);
    if (legalEntities) {
        sections.push(legalEntities);
    }

    return sections.filter(Boolean);
};
